import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { queryKeys } from 'src/api/db/queryKeys'
import { STALE_TIME_24_HOURS } from 'src/pages/Products/consts'
import {
  buildCategoriesParamFromCategoryId,
  categoryMatchSortScore,
  categoryTitleMatchesDraft,
  type CategoryPathRow,
} from 'src/pages/Products/utils'

const MAX_CATEGORY_SUGGESTIONS = 8

export type CategorySuggestion = {
  id: number
  title: string | null
  categoriesParam: string
}

export type ProductSuggestionRow = {
  id: number
  article_number: string | null
  item_name_translations: string | null
}

/** Same payload as CategoryViewer so TanStack cache is shared. */
export const useCatalogCategoriesForAutocomplete = () => {
  const supabase = useSupabaseClient()

  return useQuery({
    queryKey: [queryKeys.categories, 'all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('category')
        .select('id, title, parent_category_id, color')
        .order('title', { ascending: true })
      if (error) throw error
      return data ?? []
    },
    staleTime: STALE_TIME_24_HOURS,
    refetchOnWindowFocus: false,
  })
}

export const useCategorySuggestions = (
  draftTrimmed: string,
  categoryRows: Array<
    CategoryPathRow & { title: string | null; color?: string | null }
  >
) => {
  return useMemo(() => {
    const d = draftTrimmed.toLowerCase()
    if (!d) return []

    const rows: CategoryPathRow[] = categoryRows.map((r) => ({
      id: r.id,
      parent_category_id: r.parent_category_id,
    }))

    const matches: CategorySuggestion[] = []
    for (const row of categoryRows) {
      if (!categoryTitleMatchesDraft(row.title, d)) continue
      const param = buildCategoriesParamFromCategoryId(row.id, rows)
      if (!param) continue
      matches.push({
        id: row.id,
        title: row.title ?? null,
        categoriesParam: param,
      })
    }

    matches.sort(
      (a, b) =>
        categoryMatchSortScore(a.title, d) - categoryMatchSortScore(b.title, d)
    )

    return matches.slice(0, MAX_CATEGORY_SUGGESTIONS)
  }, [categoryRows, draftTrimmed])
}

export const useCatalogProductSuggestions = (
  debouncedTerm: string,
  enabled: boolean
) => {
  const supabase = useSupabaseClient()
  const term = debouncedTerm.trim()

  return useQuery({
    queryKey: [queryKeys.products, queryKeys.catalogAutocomplete, term],
    queryFn: async () => {
      // @ts-ignore - product_open view
      let query = supabase.from('product_open').select(
        'id, article_number, item_name_translations'
      )

      // Substring only on name and article. Description + sequential ilike caused false positives.
      query = query.or(
        `article_number.ilike.%${term}%,item_name_translations.ilike.%${term}%`
      )

      const { data, error } = await query
        .limit(8)
        .order('created_at', { ascending: false, nullsFirst: false })
        .or('is_draft.is.null,is_draft.eq.false')

      if (error) throw error

      return (data ?? []).flatMap((p) =>
        p.id == null
          ? []
          : [
              {
                id: p.id,
                article_number: p.article_number,
                item_name_translations: p.item_name_translations,
              } satisfies ProductSuggestionRow,
            ]
      )
    },
    enabled: enabled && term.length >= 2,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  })
}
